import { Chart } from 'react-google-charts';

import { useData } from '../../utils/hooks/useData';
import { useAnalyticsStoreContext } from '../contexts';

import AnalyticsError from '../AnalyticsError';
import { formatFromAndToQuery, getChartOptions } from '../lib';
import { useMergedData } from '../../utils/hooks/useMergedData';

export default function RecentApplications({ endpoints }) {
  const {
    admin_job_applications_path,
    admin_job_application_with_id_path,
    recent_applications_list_api_v1_cms_analytics_path,
    recent_applications_traffic_api_v1_cms_analytics_path,
  } = endpoints;

  const dateRange = useAnalyticsStoreContext((state) => state.data.first.dateRange);
  const dateRangeTwo = useAnalyticsStoreContext((state) => state.data.second.dateRange);
  const isComparing = useAnalyticsStoreContext((state) => state.isComparing);

  const {
    data: recentApplicationsList,
    isLoading: isRecentApplicationsListLoading,
    error: recentApplicationsListError,
  } = useData(`${recent_applications_list_api_v1_cms_analytics_path}.json?${formatFromAndToQuery(dateRange)}`);

  const {
    data: recentApplicationsTraffic,
    isLoading: isRecentApplicationsTrafficLoading,
    error: recentApplicationsTrafficError,
  } = useData(`${recent_applications_traffic_api_v1_cms_analytics_path}.json?${formatFromAndToQuery(dateRange)}`);

  const {
    data: recentApplicationsListTwo,
    isLoading: isRecentApplicationsListLoadingTwo,
    error: recentApplicationsListErrorTwo,
  } = useData(
    `${recent_applications_list_api_v1_cms_analytics_path}.json?${formatFromAndToQuery(dateRangeTwo)}`,
    isComparing && dateRangeTwo.from && dateRangeTwo.to,
    [],
  );

  const {
    data: recentApplicationsTrafficTwo,
    isLoading: isRecentApplicationsTrafficLoadingTwo,
    error: recentApplicationsTrafficErrorTwo,
  } = useData(
    `${recent_applications_traffic_api_v1_cms_analytics_path}.json?${formatFromAndToQuery(dateRangeTwo)}`,
    isComparing && dateRangeTwo.from && dateRangeTwo.to,
    [],
  );

  const chartData = useMergedData(recentApplicationsTraffic, recentApplicationsTrafficTwo, isComparing, null, { merge: true });

  return (
    <div className="card">
      <div className="card-header">Recent Applications</div>
      <div className="card-body">
        {isRecentApplicationsTrafficLoading || isRecentApplicationsTrafficLoadingTwo ? (
          <div className="d-flex align-items-center justify-content-center" style={{ height: 100 }}>
            <div className="spinner-border" role="loading">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : recentApplicationsTrafficError || recentApplicationsTrafficErrorTwo ? (
          <div className="d-flex flex-column align-items-center justify-content-center" style={{ height: 100 }}>
            {recentApplicationsTrafficError && (
              <div className="d-flex gap-2">
                <span>First Dataset:</span>
                <AnalyticsError errorMsg={recentApplicationsTrafficError} />
              </div>
            )}
            {recentApplicationsTrafficErrorTwo && (
              <div className="d-flex gap-2">
                <span>Second Dataset:</span>
                <AnalyticsError errorMsg={recentApplicationsTrafficErrorTwo} />
              </div>
            )}
          </div>
        ) : (
          <Chart width="100%" height={100} chartType="ColumnChart" data={chartData} options={getChartOptions('applications', dateRange, isComparing)} />
        )}
      </div>
      <table className="table table-sm card-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Job</th>
            <th>Source</th>
          </tr>
        </thead>
        <tbody>
          {isRecentApplicationsListLoading || isRecentApplicationsListLoadingTwo ? (
            <tr>
              <td colSpan={3} className="text-center">
                <div className="spinner-border" role="loading">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </td>
            </tr>
          ) : recentApplicationsListError || recentApplicationsListErrorTwo ? (
            <tr>
              <td colSpan={3} className="text-center">
                <div className="w-100">
                  <div className="d-flex flex-column align-items-center justify-content-center">
                    {recentApplicationsListError && (
                      <div className="d-flex gap-2">
                        <span>First Dataset:</span>
                        <AnalyticsError errorMsg={recentApplicationsListError} />
                      </div>
                    )}
                    {recentApplicationsListErrorTwo && (
                      <div className="d-flex gap-2">
                        <span>Second Dataset:</span>
                        <AnalyticsError errorMsg={recentApplicationsListErrorTwo} />
                      </div>
                    )}
                  </div>
                </div>
              </td>
            </tr>
          ) : recentApplicationsList.length === 0 ? (
            <tr>
              <td colSpan={3} className="text-center">
                No recent applications
              </td>
            </tr>
          ) : (
            recentApplicationsList.map((jobApp) => (
              <tr key={jobApp.id}>
                <td>{new Date(jobApp.created_at).toLocaleDateString()}</td>
                <td className="w-50">
                  <a href={admin_job_application_with_id_path.replace(':id', jobApp.id)}>{jobApp.job_title ? jobApp.job_title : 'Job deleted'}</a>
                </td>
                <td>{jobApp.source}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <div className="card-footer text-end">
        <a href={admin_job_applications_path}>View all...</a>
      </div>
    </div>
  );
}
